import { Injectable, inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import ValidatorUtils from '@core/utils/validator.utils';
import {
  DvCheckboxElement,
  DvDatePeriodPickerElement,
  DvDatePeriods,
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvNumberboxElement,
  DvSpaceElement,
  DvTextboxElement,
  DvTimePickerElement,
  FormOf,
} from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import { WeekdaysFormKeys } from '@pages/reports-page/components/report-tabs/report-information-tab/constants';
import {
  FilterInterval,
  PeriodInterval,
} from '@shared/components/edit-trip-filter-dialog/constants';

import { DataFilterCreateEditForm } from '@shared/components/edit-trip-filter-dialog/interfaces';
import { dvNumberMaskPresets } from '@devinco/ui/dds';
import { activeRecordFilter } from '@shared/constants/active-record-filter';

@Injectable({ providedIn: 'root' })
export class DataFilterElementsService {
  private translate = inject(TranslateService);
  private api = inject(RestApiService);

  getFilterNameElements = (): DvDynamicFormElement[] => [
    new DvTextboxElement({
      colspan: 6,
      key: 'Name',
      label: 'Report.FilterEdit.Name',
      validators: [Validators.required],
    }),
    new DvTextboxElement({
      colspan: 6,
      key: 'Description',
      label: 'Report.FilterEdit.Description',
    }),
  ];

  departmentAndDriverElements = (
    departmentColspan?: number,
    driverColspan?: number,
    departmentPlaceholder?: string,
    driverPlaceholder?: string,
    showLabels = true,
    hasPermission = false
  ): DvDynamicFormElement[] => [
    new DvDropdownClientSideElement({
      colspan: departmentColspan ?? 6,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      placeholder: departmentPlaceholder ?? '',
      key: 'Department',
      appendTo: 'body',
      label: showLabels ? 'Report.Department' : '',
      isMultipleSelectionEnabled: true,
      valueKey: 'DepartmentID',
      labelKeys: ['DepartmentDescription'],
      disabled: hasPermission,
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      colspan: driverColspan ?? 6,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'Driver',
      appendTo: 'body',
      placeholder: driverPlaceholder ?? '',
      label: showLabels ? 'Report.Driver' : '',
      isMultipleSelectionEnabled: true,
      labelKeys: ['UserNameDescription'],
      valueKey: 'UserID',
      disabled: hasPermission,
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.User,
          fields: ['UserID', 'UserNameDescription'],
        };
        this.api.setDropdownData(request, params);
      },
    }),
  ];

  getPeriodElements = (
    periodColspan?: number,
    periodPlaceholder?: string,
    showPeriodLabel = true,
    weekdaysColspan = 1,
    isTripsFilterPageElementsList = false
  ): DvDynamicFormElement[] => [
    isTripsFilterPageElementsList
      ? new DvDatePeriodPickerElement({
          key: 'Period',
          colspan: periodColspan ?? 4,
          filters: DvDatePeriods,
          dateTranslation: this.translate.instant('Report.FilterEdit.Period'),
          label: showPeriodLabel
            ? this.translate.instant('Report.FilterEdit.Period')
            : '',
        })
      : new DvDropdownClientSideElement({
          colspan: periodColspan ?? 4,
          key: 'Period',
          label: showPeriodLabel ? 'Report.FilterEdit.Period' : '',
          labelKeys: ['label'],
          valueKey: 'value',
          appendTo: 'body',
          placeholder: periodPlaceholder ?? '',
          items: [
            {
              value: PeriodInterval.Today,
              label: this.translate.instant('General.Today'),
            },
            {
              value: PeriodInterval.Yesterday,
              label: this.translate.instant('General.Yesterday'),
            },
            {
              value: PeriodInterval.ThisYear,
              label: this.translate.instant('General.ThisYear'),
            },
            {
              value: PeriodInterval.LastYear,
              label: this.translate.instant('General.LastYear'),
            },
            {
              value: PeriodInterval.ThisWeek,
              label: this.translate.instant('General.ThisWeek'),
            },
            {
              value: PeriodInterval.ThisMonth,
              label: this.translate.instant('General.ThisMonth'),
            },
            {
              value: PeriodInterval.LastWeek,
              label: this.translate.instant('General.LastWeek'),
            },
            {
              value: PeriodInterval.LastMonth,
              label: this.translate.instant('General.LastMonth'),
            },
            {
              value: PeriodInterval.Custom,
              label: this.translate.instant('General.Custom'),
            },
          ],
        }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Mon,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Monday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Tue,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Tuesday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Wed,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Wednesday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Thu,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Thursday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Fri,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Friday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Sat,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Saturday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Sun,
      colspan: weekdaysColspan,
      checkboxLabel: 'General.DaysOfWeekShort.Sunday',
      extra: { ignoreInRequest: true },
    }),
    new DvTextboxElement({
      key: 'WeekDay',
      hidden: true,
    }),
    new DvNumberboxElement({
      key: 'StartDay',
      colspan: 4,
      hidden: true,
      extra: { IsActivatable: true },
      label: 'Report.FilterEdit.StartDay',
      validators: [Validators.required],
    }),
    new DvNumberboxElement({
      key: 'EndDay',
      colspan: 4,
      hidden: true,
      extra: { IsActivatable: true },
      label: 'Report.FilterEdit.EndDay',
      validators: [Validators.required],
    }),
  ];

  getIntervalElements = (elementsColspan?: number): DvDynamicFormElement[] => [
    new DvDropdownClientSideElement({
      key: 'Interval',
      label: 'Report.FilterEdit.Interval',
      colspan: elementsColspan ?? 4,
      appendTo: 'body',
      labelKeys: ['label'],
      valueKey: 'value',
      isClearable: false,
      value: FilterInterval.None,
      items: [
        {
          value: FilterInterval.None,
          label: this.translate.instant('General.None'),
        },
        {
          value: FilterInterval.Between,
          label: this.translate.instant('General.Between'),
        },
        {
          value: FilterInterval.Outside,
          label: this.translate.instant('General.Outside'),
        },
        {
          value: FilterInterval.StartBefore,
          label: this.translate.instant('General.StartBefore'),
        },
        {
          value: FilterInterval.StartAfter,
          label: this.translate.instant('General.StartAfter'),
        },
        {
          value: FilterInterval.EndBefore,
          label: this.translate.instant('General.EndBefore'),
        },
        {
          value: FilterInterval.EndAfter,
          label: this.translate.instant('General.EndAfter'),
        },
      ],
    }),
    new DvTimePickerElement({
      key: 'IntervalFrom',
      colspan: elementsColspan ?? 3,
      label: 'General.From',
      validators: [ValidatorUtils.timeRangeValidator('IntervalTo')],
      clearable: true,
      disabled: true,
    }),
    new DvTimePickerElement({
      key: 'IntervalTo',
      colspan: elementsColspan ?? 3,
      label: 'General.To',
      validators: [ValidatorUtils.dateTimeRangeEndValidator('IntervalFrom')],
      clearable: true,
      disabled: true,
    }),
  ];

  getDistanceElements = (): DvDynamicFormElement[] => [
    new DvNumberboxElement({
      key: 'DistanceGreater',
      colspan: 4,
      label: 'Report.FilterEdit.DistanceGreater',
      fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
    }),
    new DvNumberboxElement({
      key: 'DistanceLess',
      colspan: 4,
      label: 'Report.FilterEdit.DistanceLess',
      fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
    }),
  ];

  getPermissionElements = (): DvDynamicFormElement[] => [
    new DvSpaceElement({
      rowspan: 3,
      colspan: 10,
    }),
    new DvCheckboxElement({
      colspan: 2,
      rowspan: 3,
      key: 'VisibleToAllUsers',
      checkboxLabel: 'Report.FilterEdit.VisibleToAllUsers',
    }),
  ];

  toggleIntervalFieldsVisibility(
    elements: DvDynamicFormElement[],
    formGroup: FormGroup<FormOf<DataFilterCreateEditForm>>
  ): DvDynamicFormElement[] {
    const toControl = formGroup.controls.IntervalTo;
    const fromControl = formGroup.controls.IntervalFrom;
    const interval = formGroup.controls.Interval?.value;
    fromControl?.clearValidators();
    toControl?.clearValidators();
    formGroup.updateValueAndValidity();
    switch (interval) {
      case FilterInterval.None:
        toControl?.setValue(undefined, { emitEvent: false });
        toControl?.disable();
        fromControl?.setValue(undefined, { emitEvent: false });
        fromControl?.disable();
        return elements;
      case FilterInterval.EndAfter:
      case FilterInterval.EndBefore:
        toControl?.enable();
        fromControl?.setValue(undefined, { emitEvent: false });
        fromControl?.disable();
        return elements;
      case FilterInterval.StartBefore:
      case FilterInterval.StartAfter:
        toControl?.disable();
        toControl?.setValue(undefined, { emitEvent: false });
        fromControl?.enable();
        return elements;
      case FilterInterval.Between:
      case FilterInterval.Outside:
        toControl?.setValidators([
          ValidatorUtils.dateTimeRangeEndValidator('IntervalFrom'),
        ]);
        fromControl?.setValidators([
          ValidatorUtils.timeRangeValidator('IntervalTo'),
        ]);
        formGroup.updateValueAndValidity();
        fromControl?.enable();
        toControl?.enable();
        return elements;
      default:
        fromControl?.enable();
        toControl?.enable();
        return elements;
    }
  }
}
